/**
 * Assessment Performance — Redux slice (backport, simplified).
 *
 * Owns: period A / B + active preset, comparison metrics (rows + totals),
 * fetch status. Drill-down modal + email export from the FSD page on
 * refactor/mui-v9-migration are intentionally NOT backported.
 *
 * TEMP: deleted on FSD merge — @entities/assessment-performance owns
 * the canonical slice on the FSD branch. Same name, same shape, so
 * components reading state.assessmentPerformance keep working.
 */
import { createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '../constants';
import { fetchAssessmentMetrics } from '../api';

export const PRESET_IDS = {
  custom: 'custom',
  weekOverWeek: 'wow',
  monthOverMonth: 'mom',
};

const initialState = {
  // Period date ranges as ISO-day strings ({ start, end }) — easy to
  // serialise into the redux store and easy to feed to the thunk.
  // The page converts back to native Date when feeding CalendarPopover.
  periodA: { start: null, end: null },
  periodB: { start: null, end: null },
  preset: PRESET_IDS.weekOverWeek,

  // Account scope — multi-account picker. Empty array means "no
  // selection yet" — the page renders an explicit prompt and the
  // thunk does not fire (backend requires at least one accountUuid).
  // Each entry is `{ uuid, businessName }`.
  selectedAccounts: [],

  // Metrics
  rows: [],
  totals: null,
  generatedAt: null,
  metricsStatus: REQUEST_STATUS.idle,
  metricsError: null,
};

const assessmentPerformanceSlice = createSlice({
  name: 'assessmentPerformance',
  initialState,
  reducers: {
    setPeriodA: (state, action) => {
      state.periodA = action.payload;
    },
    setPeriodB: (state, action) => {
      state.periodB = action.payload;
    },
    setPreset: (state, action) => {
      state.preset = action.payload;
    },
    setPeriods: (state, action) => {
      const { periodA, periodB, preset } = action.payload || {};
      if (periodA) state.periodA = periodA;
      if (periodB) state.periodB = periodB;
      if (preset) state.preset = preset;
    },
    setAssessmentSelectedAccounts: (state, action) => {
      state.selectedAccounts = Array.isArray(action.payload) ? action.payload : [];
    },
    resetAssessmentPerformance: () => ({ ...initialState }),
  },
  extraReducers(builder) {
    builder
      .addCase(fetchAssessmentMetrics.pending, (state) => {
        state.metricsStatus = REQUEST_STATUS.loading;
        state.metricsError = null;
      })
      .addCase(fetchAssessmentMetrics.fulfilled, (state, action) => {
        if (action.payload?.ok) {
          state.metricsStatus = REQUEST_STATUS.succeeded;
          state.rows = action.payload.data.rows;
          state.totals = action.payload.data.totals;
          state.generatedAt = action.payload.data.generatedAt;
        } else {
          state.metricsStatus = REQUEST_STATUS.failed;
          state.metricsError =
            action.payload?.error?.message || 'Failed to load metrics';
        }
      })
      .addCase(fetchAssessmentMetrics.rejected, (state, action) => {
        state.metricsStatus = REQUEST_STATUS.failed;
        state.metricsError =
          action.payload?.error?.message ||
          action.error?.message ||
          'Failed to load metrics';
      });
  },
});

export const {
  setPeriodA,
  setPeriodB,
  setPreset,
  setPeriods,
  setAssessmentSelectedAccounts,
  resetAssessmentPerformance,
} = assessmentPerformanceSlice.actions;

export default assessmentPerformanceSlice.reducer;
